import { Theme } from '@cmctechnology/webinvest-constants';
import { BRANDValue, BrandDark, CoreDark, EORA_COLOURS, IEoraColoursBASE } from './eoraColours';
import { eoraThemeName } from '../../themes/themeNames';
import { StateColour, StateColourType } from './colourInterfaces';
import { BASIC_COLOURS, BASIC_NO_SWITCH_COLOURS, STATUS_COLOURS } from './variants';

export const interactivityColour = (tint: string, base: string) => `linear-gradient(0deg, ${tint} 0%, ${tint} 100%), ${base}`;
export interface IEoraThemeBrandColours extends Omit<BRANDValue, 'DARK' | 'LIGHT'> {
  MODE: BrandDark;
}

export interface IEoraThemeColours {
  eoraBaseColours: IEoraColoursBASE;
  eoraCoreColours: CoreDark;
  eoraBrandColours: IEoraThemeBrandColours;
}

const constructEoraStateColours = ({ colour, text, link, hover, active }: StateColour): StateColour => ({
  colour,
  text,
  link,
  hover: interactivityColour(hover, colour),
  active: interactivityColour(active, colour)
});

const extractBrandAndModeColours = (theme: Theme, darkMode: boolean) => {
  const brandColoursFull = eoraThemeName[theme] && EORA_COLOURS.BRAND[eoraThemeName[theme]] ? EORA_COLOURS.BRAND[eoraThemeName[theme]] : EORA_COLOURS.BRAND.CMC;
  const brandColoursClone = JSON.parse(JSON.stringify(brandColoursFull));

  delete brandColoursClone?.LIGHT;
  delete brandColoursClone?.DARK;

  const brandColoursBase: Omit<BRANDValue, 'DARK' | 'LIGHT'> = brandColoursClone;
  const brandColoursMode = darkMode ? brandColoursFull.DARK : brandColoursFull.LIGHT;

  return {
    ...brandColoursBase,
    MODE: brandColoursMode
  };
};

// Remove other whitelabels and either dark or light mode based on darkMode flag
export const getEoraThemeColours = (theme: Theme, darkMode: boolean): IEoraThemeColours => {
  return {
    eoraBaseColours: EORA_COLOURS.BASE,
    eoraCoreColours: darkMode ? EORA_COLOURS.CORE.DARK : EORA_COLOURS.CORE.LIGHT,
    eoraBrandColours: extractBrandAndModeColours(theme, darkMode)
  };
};

export type EoraBrandColours = {
  accent: StateColourType;
  primary: StateColourType;
  closeButtonOnPrimary: StateColourType;
};

export const getBrandColours = (colours: IEoraThemeBrandColours): EoraBrandColours => {
  const { BRAND_WL_COLORS: base, MODE: mode } = colours;

  const brandColours: EoraBrandColours = {
    accent: constructEoraStateColours({
      colour: base.WL_COLOR_ACCENT,
      text: mode.BRAND_AWARE_TEXT.WL_COLOR_ONACCENT,
      active: mode.BRAND_INTERACTIVITY.WL_COLOR_BACKGROUND_TINT_ACCENT_PRESSED,
      hover: mode.BRAND_INTERACTIVITY.WL_COLOR_BACKGROUND_TINT_ACCENT_HOVER,
      link: base.WL_COLOR_HYPERLINK_ACCENT
    }),
    primary: constructEoraStateColours({
      colour: base.WL_COLOR_PRIMARY,
      text: mode.BRAND_AWARE_TEXT.WL_COLOR_ONPRIMARY,
      active: mode.BRAND_INTERACTIVITY.BRAND_BG_COLOR_PRESSED_PRIMARY,
      hover: mode.BRAND_INTERACTIVITY.WL_COLOR_BACKGROUND_TINT_PRIMARY_HOVER,
      link: base.WL_COLOR_HYPERLINK_PRIMARY ?? base.WL_COLOR_PRIMARY
    }),
    closeButtonOnPrimary: constructEoraStateColours({
      colour: 'transparent',
      text: mode.BRAND_AWARE_CLOSE_BUTTON.WL_CLOSEBUTTON_COLOR_TEXT_ONPRIMARY,
      active: mode.BRAND_AWARE_CLOSE_BUTTON.WL_CLOSEBUTTON_COLOR_BACKGROUND_ONPRIMARY_PRESSED,
      hover: mode.BRAND_AWARE_CLOSE_BUTTON.WL_CLOSEBUTTON_COLOR_BACKGROUND_ONPRIMARY_HOVER,
      link: base.WL_COLOR_HYPERLINK_PRIMARY
    })
  };

  return brandColours;
};

type StatusColours = typeof STATUS_COLOURS.SUCCESS | typeof STATUS_COLOURS.WARNING | typeof STATUS_COLOURS.ERROR;

export type EoraBaseColours = {
  base: {
    [BASIC_COLOURS.BLACK]: StateColourType;
    [BASIC_COLOURS.WHITE]: StateColourType;
    [BASIC_NO_SWITCH_COLOURS.BLACK_NO_SWITCH]: StateColourType;
    [BASIC_NO_SWITCH_COLOURS.WHITE_NO_SWITCH]: StateColourType;
  };
  secondary: StateColourType;
  secondaryInverted: StateColourType;
  status: Record<StatusColours, StateColour>;
  opaque: {
    [BASIC_COLOURS.BLACK]: StateColourType;
    [BASIC_COLOURS.WHITE]: StateColourType;
    black50: StateColourType;
    white50: StateColourType;
    black50NoSwitch: StateColourType;
    white50NoSwitch: StateColourType;
    black50Minimal: StateColourType;
    white50Minimal: StateColourType;
    black50NoSwitchMinimal: StateColourType;
    white50NoSwitchMinimal: StateColourType;
  };
  palette: {
    neutral900: string;
    neutral800: string;
    neutral700: string;
    neutral600: string;
    neutral: string;
    neutral400: string;
    neutral300: string;
    neutral200: string;
    neutral100: string;
    neutral50: string;
    neutral35: string;
    neutral25: string;
    neutral15: string;
    neutral05: string;
  };
  text: {
    dark: string;
    default: string;
    defaultNoSwitch: string;
    light: string;
    error: string;
    disabled: string;
    disabledNoSwitch: string;
  };
  trading: {
    positive: string;
    negative: string;
    unchanged: string;
  };
};

/**
 * baseColours override on theme to gradually replace current baseColours object while maintaining backward compatibility
 */
export const getBaseColours = (eoraCoreColours: CoreDark, darkMode: boolean): EoraBaseColours => ({
  base: {
    black: constructEoraStateColours({
      colour: eoraCoreColours.BASE.BLACK,
      text: eoraCoreColours.BASE.WHITE,
      active: eoraCoreColours.BACKGROUND_COLOR.INTERACTIVE_STATES_BG.COLOR_BACKGROUND_TINT_PRESSED_LIGHT,
      hover: eoraCoreColours.BACKGROUND_COLOR.INTERACTIVE_STATES_BG.COLOR_BACKGROUND_TINT_HOVER_LIGHT
    }),
    white: constructEoraStateColours({
      colour: eoraCoreColours.BASE.WHITE,
      text: eoraCoreColours.BASE.BLACK,
      active: eoraCoreColours.BACKGROUND_COLOR.INTERACTIVE_STATES_BG.COLOR_BACKGROUND_TINT_PRESSED_DARK,
      hover: eoraCoreColours.BACKGROUND_COLOR.INTERACTIVE_STATES_BG.COLOR_BACKGROUND_TINT_HOVER_DARK
    }),
    blackNoSwitch: constructEoraStateColours({
      colour: eoraCoreColours.BASE.BLACK_NOSWITCH,
      text: eoraCoreColours.BASE.WHITE_NOSWITCH,
      active: eoraCoreColours.BACKGROUND_COLOR.INTERACTIVE_STATES_BG.COLOR_BACKGROUND_TINT_PRESSED_LIGHT_NOSWITCH,
      hover: eoraCoreColours.BACKGROUND_COLOR.INTERACTIVE_STATES_BG.COLOR_BACKGROUND_TINT_HOVER_LIGHT_NOSWITCH
    }),
    whiteNoSwitch: constructEoraStateColours({
      colour: eoraCoreColours.BASE.WHITE_NOSWITCH,
      text: eoraCoreColours.BASE.BLACK_NOSWITCH,
      active: eoraCoreColours.BACKGROUND_COLOR.INTERACTIVE_STATES_BG.COLOR_BACKGROUND_TINT_PRESSED_DARK_NOSWITCH,
      hover: eoraCoreColours.BACKGROUND_COLOR.INTERACTIVE_STATES_BG.COLOR_BACKGROUND_TINT_HOVER_DARK_NOSWITCH
    })
  },
  secondary: constructEoraStateColours({
    colour: eoraCoreColours.BACKGROUND_COLOR.SECONDARY_BASE.COLOR_BACKGROUND_SECONDARYBASE,
    text: eoraCoreColours.TEXT_COLOR.COLOR_TEXT_DEFAULT,
    active: eoraCoreColours.BACKGROUND_COLOR.INTERACTIVE_STATES_BG.COLOR_BACKGROUND_TINT_HOVER_DARK,
    hover: eoraCoreColours.BACKGROUND_COLOR.INTERACTIVE_STATES_BG.COLOR_BACKGROUND_TINT_PRESSED_DARK
  }),
  secondaryInverted: constructEoraStateColours({
    colour: eoraCoreColours.BACKGROUND_COLOR.SECONDARY_BASE.COLOR_BACKGROUND_SECONDARYBASE_INVERTED,
    text: eoraCoreColours.BASE.WHITE,
    active: eoraCoreColours.BACKGROUND_COLOR.INTERACTIVE_STATES_BG.COLOR_BACKGROUND_TINT_HOVER_LIGHT,
    hover: eoraCoreColours.BACKGROUND_COLOR.INTERACTIVE_STATES_BG.COLOR_BACKGROUND_TINT_PRESSED_LIGHT
  }),
  status: {
    error: constructEoraStateColours({
      colour: eoraCoreColours.STATUS_COLORS.ERROR.COLOR_ERROR_600,
      text: eoraCoreColours.BASE.WHITE_NOSWITCH,
      active: eoraCoreColours.BACKGROUND_COLOR.INTERACTIVE_STATES_BG.COLOR_BACKGROUND_TINT_PRESSED_DARK_NOSWITCH,
      hover: eoraCoreColours.BACKGROUND_COLOR.INTERACTIVE_STATES_BG.COLOR_BACKGROUND_TINT_HOVER_DARK_NOSWITCH
    }),
    warning: constructEoraStateColours({
      colour: eoraCoreColours.STATUS_COLORS.WARNING.COLOR_WARNING_600,
      text: eoraCoreColours.BASE.WHITE_NOSWITCH,
      active: eoraCoreColours.BACKGROUND_COLOR.INTERACTIVE_STATES_BG.COLOR_BACKGROUND_TINT_PRESSED_DARK_NOSWITCH,
      hover: eoraCoreColours.BACKGROUND_COLOR.INTERACTIVE_STATES_BG.COLOR_BACKGROUND_TINT_HOVER_DARK_NOSWITCH
    }),
    success: constructEoraStateColours({
      colour: eoraCoreColours.STATUS_COLORS.SUCCESS.COLOR_SUCCESS_600,
      text: eoraCoreColours.BASE.WHITE_NOSWITCH,
      active: eoraCoreColours.BACKGROUND_COLOR.INTERACTIVE_STATES_BG.COLOR_BACKGROUND_TINT_PRESSED_DARK_NOSWITCH,
      hover: eoraCoreColours.BACKGROUND_COLOR.INTERACTIVE_STATES_BG.COLOR_BACKGROUND_TINT_HOVER_DARK_NOSWITCH
    })
  },
  opaque: {
    black: constructEoraStateColours({
      colour: eoraCoreColours.OPACITIES.COLOR_DARK_OPACITY_60,
      text: eoraCoreColours.BASE.WHITE,
      active: eoraCoreColours.OPACITIES.COLOR_DARK_OPACITY_70,
      hover: eoraCoreColours.BASE.BLACK
    }),
    white: constructEoraStateColours({
      colour: eoraCoreColours.OPACITIES.COLOR_LIGHT_OPACITY_60,
      text: eoraCoreColours.BASE.BLACK,
      active: eoraCoreColours.OPACITIES.COLOR_LIGHT_OPACITY_70,
      hover: eoraCoreColours.BASE.WHITE
    }),
    black50: constructEoraStateColours({
      colour: eoraCoreColours.OPACITIES.COLOR_DARK_OPACITY_50,
      text: eoraCoreColours.BASE.WHITE,
      active: 'eoraCoreColours.OPACITIES.COLOR_DARK_OPACITY_70',
      hover: eoraCoreColours.OPACITIES.COLOR_DARK_OPACITY_60
    }),
    white50: constructEoraStateColours({
      colour: eoraCoreColours.OPACITIES.COLOR_LIGHT_OPACITY_50,
      text: eoraCoreColours.BASE.BLACK,
      active: eoraCoreColours.OPACITIES.COLOR_LIGHT_OPACITY_70,
      hover: eoraCoreColours.OPACITIES.COLOR_LIGHT_OPACITY_60
    }),
    black50NoSwitch: constructEoraStateColours({
      colour: eoraCoreColours.OPACITIES.COLOR_DARK_OPACITY_50_NOSWITCH,
      text: eoraCoreColours.BASE.WHITE_NOSWITCH,
      active: eoraCoreColours.OPACITIES.COLOR_DARK_OPACITY_70_NOSWITCH,
      hover: eoraCoreColours.OPACITIES.COLOR_DARK_OPACITY_60_NOSWITCH
    }),
    white50NoSwitch: constructEoraStateColours({
      colour: eoraCoreColours.OPACITIES.COLOR_LIGHT_OPACITY_50_NOSWITCH,
      text: eoraCoreColours.BASE.BLACK_NOSWITCH,
      active: eoraCoreColours.OPACITIES.COLOR_LIGHT_OPACITY_70_NOSWITCH,
      hover: eoraCoreColours.OPACITIES.COLOR_LIGHT_OPACITY_60_NOSWITCH
    }),
    black50Minimal: constructEoraStateColours({
      colour: 'transparent',
      text: eoraCoreColours.BASE.BLACK,
      active: eoraCoreColours.OPACITIES.COLOR_DARK_OPACITY_20,
      hover: eoraCoreColours.OPACITIES.COLOR_DARK_OPACITY_10
    }),
    white50Minimal: constructEoraStateColours({
      colour: 'transparent',
      text: eoraCoreColours.BASE.WHITE,
      active: eoraCoreColours.OPACITIES.COLOR_LIGHT_OPACITY_20,
      hover: eoraCoreColours.OPACITIES.COLOR_LIGHT_OPACITY_10
    }),
    black50NoSwitchMinimal: constructEoraStateColours({
      colour: 'transparent',
      text: eoraCoreColours.BASE.BLACK_NOSWITCH,
      active: eoraCoreColours.OPACITIES.COLOR_DARK_OPACITY_20_NOSWITCH,
      hover: eoraCoreColours.OPACITIES.COLOR_DARK_OPACITY_10_NOSWITCH
    }),
    white50NoSwitchMinimal: constructEoraStateColours({
      colour: 'transparent',
      text: eoraCoreColours.BASE.WHITE_NOSWITCH,
      active: eoraCoreColours.OPACITIES.COLOR_LIGHT_OPACITY_20_NOSWITCH,
      hover: eoraCoreColours.OPACITIES.COLOR_LIGHT_OPACITY_10_NOSWITCH
    })
  },
  palette: {
    neutral900: eoraCoreColours.NEUTRAL.COLOR_NEUTRAL_900,
    neutral800: eoraCoreColours.NEUTRAL.COLOR_NEUTRAL_800,
    neutral700: eoraCoreColours.NEUTRAL.COLOR_NEUTRAL_700,
    neutral600: eoraCoreColours.NEUTRAL.COLOR_NEUTRAL_600,
    neutral: eoraCoreColours.NEUTRAL.COLOR_NEUTRAL_500,
    neutral400: eoraCoreColours.NEUTRAL.COLOR_NEUTRAL_400,
    neutral300: eoraCoreColours.NEUTRAL.COLOR_NEUTRAL_300,
    neutral200: eoraCoreColours.NEUTRAL.COLOR_NEUTRAL_200,
    neutral100: eoraCoreColours.NEUTRAL.COLOR_NEUTRAL_100,
    neutral50: eoraCoreColours.NEUTRAL.COLOR_NEUTRAL_50,
    neutral35: eoraCoreColours.NEUTRAL.COLOR_NEUTRAL_35,
    neutral25: eoraCoreColours.NEUTRAL.COLOR_NEUTRAL_25,
    neutral15: eoraCoreColours.NEUTRAL.COLOR_NEUTRAL_15,
    neutral05: eoraCoreColours.NEUTRAL.COLOR_NEUTRAL_05
  },
  text: {
    dark: eoraCoreColours.TEXT_COLOR.COLOR_TEXT_DARK,
    default: eoraCoreColours.TEXT_COLOR.COLOR_TEXT_DEFAULT,
    defaultNoSwitch: eoraCoreColours.TEXT_COLOR.COLOR_TEXT_DEFAULT_NOSWITCH,
    light: eoraCoreColours.TEXT_COLOR.COLOR_TEXT_LIGHT,
    error: eoraCoreColours.TEXT_COLOR.COLOR_TEXT_ERROR,
    disabled: eoraCoreColours.TEXT_COLOR.COLOR_TEXT_DISABLED,
    disabledNoSwitch: eoraCoreColours.TEXT_COLOR.COLOR_TEXT_DISABLED_NOSWITCH
  },
  trading: {
    positive: darkMode ? eoraCoreColours.TRADING_COLORS.COLOR_TRADING_POSITIVE : eoraCoreColours.TRADING_COLORS.COLOR_TRADING_POSITIVE_DARK,
    negative: darkMode ? eoraCoreColours.TRADING_COLORS.COLOR_TRADING_NEGATIVE : eoraCoreColours.TRADING_COLORS.COLOR_TRADING_NEGATIVE_DARK,
    unchanged: eoraCoreColours.TEXT_COLOR.COLOR_TEXT_DEFAULT
  }
});
